/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull web'
 *
 * Required dependencies: amplitude-js@^8.18.0
 * Tracking Plan Version: 44
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli
 *
 * [View Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest)
 *
 * [Full Setup Instructions](https://data.amplitude.com/gotrah/Waffy/implementation/web)
 */

import amplitude, { AmplitudeClient, Callback, Config } from "amplitude-js";
import { fetchAndActivate, getValue } from "firebase/remote-config";

import { remoteConfig } from "../firebase/firebase";
import { AMPLITUDE_API_KEY } from "./constants";
import { stringList } from "aws-sdk/clients/datapipeline";

export type Environment = "development" | "production";

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultOptions: ConfigExt = {
  plan: {
    version: "44",
    branch: "main",
    source: "web",
    versionId: "4ec3d650-d216-4698-a50c-ea6a1d4e7d79",
  },
};

export interface LoadOptions {
  environment?: Environment;
  disabled?: boolean;
  client?: {
    apiKey?: string;
    options?: Partial<ConfigExt>;
    instance?: AmplitudeClient;
  };
}

export interface IdentifyProperties {
  /**
   * during regitration or user profile
   */
  absherVerificationDate?: string;
  biometricEnabled?: boolean;
  /**
   * during regitration or user profile
   */
  isAbsherVerified?: boolean;
  isPushNotificationEnabled?: boolean;
  signupDate?: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  totalNumberOfContractCreated?: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  totalNumberOfContractDeleted?: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  totalNumberOfDeliveryConfirmation?: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  totalNumberOfEscalation?: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | number |
   */
  totalNumberOfInspectionConfirmation?: number;
  /**
   * full name during registartion or when the user edit his profile
   *
   *  Regitration and edit profile
   */
  userName?: string;
  /**
   * during regitration
   */
  userPhoneNumber?: string;

  overallFeedbackScore?: number;
}

export interface AdminRefundProperties {
  contractId: string;
  refundDate: string;
}

export enum ResolveState {
  Release = "Release",
  Refund = "Refund",
}

export interface AdminResolveEscalationProperties {
  adminResolveDate: string;
  buyerId: string;
  contractId: string;
  resolveState: ResolveState;
  sellerId: string;
}

export interface AdminRefundBuyerProperties {
  adminRefundDate: string;
  buyerId: string;
  contractId: string;
  sellerId: string;
}

export interface AdminReleaseSellerProperties {
  adminRefundDate: string;
  buyerId: string;
  contractId: string;
  sellerId: string;
}

export interface AdminReleaseProperties {
  contractId: string;
  releaseDate: string;
}

export interface AdminResolveProperties {
  contractId: string;
  resolveDate: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | Release, Refund |
   */
  resolveState: "Release" | "Refund";
}

export interface EndedBrokerContractProperties {
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | Release, Refund |
   */
  brokerContractClosure: "Release" | "Refund";
}

export interface SubmitPhoneNumberProperties {
  userPhoneNumber: string;
  otpMethod: string;
}
export interface ValidatedOTPProperties {
  isOtpValid: boolean;
}

export interface ConfirmedDeliveryProperties {
  contractId: string;
  deliveryConfirmationDate: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}

export interface AddedPersonalInfoProperties {
  firtName: string;
  lastName: string;
  uploadedPersonalImage: boolean;
}

export interface ConfirmedInspectionProperties {
  contractId: string;
  inspectionConfirmationDate: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}

export interface CreatedContractProperties {
  contractCreationDate: string;
  contractId: string;
  isShipmentCompanyEnabled: boolean;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  numOfproductImageAttachement?: number;
  productTitle: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | number |
   */
  shipmentFees: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | number |
   */
  ticketSize: number;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | number |
   */
  waffyServiceFees: number;
}

export interface DeletedContractProperties {
  contractDeletionDate: string;
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}

export interface EscalatedProperties {
  additionalEscalationReasons?: string;
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | productNotDelivered, productNotIdenticalWithDescription, productNotWorking, productHasCracksAndDefects |
   */
  escalationReasons?: string[];
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | delivery, inspection |
   */
  escaltionType: "delivery" | "inspection";
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}

export interface SentFeedbackProperties {
  contractId: string;
  contractStatus: EAmpliContractStatus;
  ratingScore: number;
  userRole: EAmpliUserRole;
}
export interface PayedContractProperties {
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | number |
   */
  paymentAmount: number;
  paymentDate: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | DC, CD |
   */
  paymentMethod: "DC" | "CD";
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}

export interface SharedContractProperties {
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | Whatsapp, Messanger, Instagram, Others |
   */
  shareContractMethod: "Whatsapp" | "Messanger" | "Instagram" | "Others";
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | contractDetailsPage, contractWizardPage |
   */
  shareContractPageSource: "contractDetailsPage" | "contractWizardPage";
}

export interface SignedUpProperties {
  enableBiometric?: boolean;
  isAbsherVerified: boolean;
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  resendRegistrationOtpTrials?: number;
  signUpDate: string;
  userName: string;
  userPhoneNumber: string;
}

export interface VerifiedViaAbsherProperties {
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | signUpPage, userProfilePage |
   */
  absherPageSource: "signUpPage" | "userProfilePage";
  /**
   * | Rule | Value |
   * |---|---|
   * | Type | integer |
   */
  resendAbsherOtpTrials: number;
}

export interface ViewedContractDetailsPageProperties {
  contractId: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | active, waitingForDelivery, waitingForInspection, itemAccepted, released, escalated, escalationResolved, closeRefund, closeRelease |
   */
  contractStatus: EAmpliContractStatus;
  /**
   * | Rule | Value |
   * |---|---|
   * | Enum Values | seller, buyer, admin |
   */
  userRole: "seller" | "buyer" | "admin";
}
export enum EAmpliContractStatus {
  active = "active",
  waitingForDelivery = "waitingForDelivery",
  waitingForInspection = "waitingForInspection",
  itemAccepted = "itemAccepted",
  released = "released",
  escalated = "escalated",
  escalationResolved = "escalationResolved",
  closeRefund = "closeRefund",
  closeRelease = "closeRelease",
}

export enum EAmpliUserRole {
  seller = "seller",
  buyer = "buyer",
  admin = "admin",
}
export class Identify implements BaseEvent {
  event_type = "Identify";

  constructor(public event_properties?: IdentifyProperties) {
    this.event_properties = event_properties;
  }
}

export class Group implements BaseEvent {
  event_type = "Group";
}

export class AdminConfirmDelivery implements BaseEvent {
  event_type = "Admin Confirm Delivery";
}

export class AdminConfirmInspection implements BaseEvent {
  event_type = "Admin Confirm Inspection";
}

export class AdminRefund implements BaseEvent {
  event_type = "Admin Refund";

  constructor(public event_properties: AdminRefundProperties) {
    this.event_properties = event_properties;
  }
}

export class AdminResolveEscalation implements BaseEvent {
  event_type = "Admin Resolve Escalation";

  constructor(public event_properties: AdminResolveEscalationProperties) {
    this.event_properties = event_properties;
  }
}

export class AdminRefundBuyer implements BaseEvent {
  event_type = "Admin Refund Buyer";

  constructor(public event_properties: AdminRefundBuyerProperties) {
    this.event_properties = event_properties;
  }
}

export class AdminReleaseSeller implements BaseEvent {
  event_type = "Admin Release Seller";

  constructor(public event_properties: AdminReleaseSellerProperties) {
    this.event_properties = event_properties;
  }
}

export class AdminRelease implements BaseEvent {
  event_type = "Admin Release";

  constructor(public event_properties: AdminReleaseProperties) {
    this.event_properties = event_properties;
  }
}

export class AdminResolve implements BaseEvent {
  event_type = "Admin Resolve";

  constructor(public event_properties: AdminResolveProperties) {
    this.event_properties = event_properties;
  }
}

export class EndedBrokerContract implements BaseEvent {
  event_type = "Ended Broker Contract";

  constructor(public event_properties: EndedBrokerContractProperties) {
    this.event_properties = event_properties;
  }
}

export class SubmitPhoneNumber implements BaseEvent {
  event_type = "Submit Phone Number";

  constructor(public event_properties: SubmitPhoneNumberProperties) {
    this.event_properties = event_properties;
  }
}

export class ValidatedOTP implements BaseEvent {
  event_type = "Validated OTP";

  constructor(public event_properties: ValidatedOTPProperties) {
    this.event_properties = event_properties;
  }
}

export class AddedPinCode implements BaseEvent {
  event_type = "Added Pin Code";
}
export class ConfirmedDelivery implements BaseEvent {
  event_type = "Confirmed Delivery";

  constructor(public event_properties: ConfirmedDeliveryProperties) {
    this.event_properties = event_properties;
  }
}

export class AddedPersonalInfo implements BaseEvent {
  event_type = "Added Personal Info";

  constructor(public event_properties: AddedPersonalInfoProperties) {
    this.event_properties = event_properties;
  }
}

export class ConfirmedInspection implements BaseEvent {
  event_type = "Confirmed Inspection";

  constructor(public event_properties: ConfirmedInspectionProperties) {
    this.event_properties = event_properties;
  }
}

export class CreatedContract implements BaseEvent {
  event_type = "Created Contract";

  constructor(public event_properties: CreatedContractProperties) {
    this.event_properties = event_properties;
  }
}

export class DeletedContract implements BaseEvent {
  event_type = "Deleted Contract";

  constructor(public event_properties: DeletedContractProperties) {
    this.event_properties = event_properties;
  }
}

export class Escalated implements BaseEvent {
  event_type = "Escalated";

  constructor(public event_properties: EscalatedProperties) {
    this.event_properties = event_properties;
  }
}

export class SentFeedback implements BaseEvent {
  event_type = "Sent Feedback";

  constructor(public event_properties: SentFeedbackProperties) {
    this.event_properties = event_properties;
  }
}

export class PayedContract implements BaseEvent {
  event_type = "Payed Contract";

  constructor(public event_properties: PayedContractProperties) {
    this.event_properties = event_properties;
  }
}

export class SharedContract implements BaseEvent {
  event_type = "Shared Contract";

  constructor(public event_properties: SharedContractProperties) {
    this.event_properties = event_properties;
  }
}

export class SignedUp implements BaseEvent {
  event_type = "Signed Up";

  constructor(public event_properties: SignedUpProperties) {
    this.event_properties = event_properties;
  }
}

export class VerifiedViaAbsher implements BaseEvent {
  event_type = "Verified Via Absher";

  constructor(public event_properties: VerifiedViaAbsherProperties) {
    this.event_properties = event_properties;
  }
}

export class ViewedContractDetailsPage implements BaseEvent {
  event_type = "Viewed Contract Details Page";

  constructor(public event_properties: ViewedContractDetailsPageProperties) {
    this.event_properties = event_properties;
  }
}

export class ViewedContractWizardPage implements BaseEvent {
  event_type = "Viewed Contract Wizard Page";
}

export class ViewedSignUpPage implements BaseEvent {
  event_type = "Viewed Sign Up Page";
}

// prettier-ignore
export class Ampli {
  private disabled = false;
  private amplitude?: AmplitudeClient;
  private middlewares: Middleware[] = [];

  get client(): AmplitudeClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.isLoaded) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options?: LoadOptions): void {
    this.disabled = options?.disabled ?? false;

    if (this.isLoaded) {
      console.warn('WARNING: Ampli is already initialized. Ampli.load() should be called once at application startup.');
      return;
    }
    fetchAndActivate(remoteConfig).then(() => {
      const result = getValue(remoteConfig, AMPLITUDE_API_KEY) as unknown as {
        _source: string;
        _value: string;
      };
      const apiKey = result._value;
      if (options?.client?.instance) {
        this.amplitude = options?.client?.instance;
      } else if (apiKey) {
        this.amplitude = amplitude.getInstance();
        this.amplitude?.init(apiKey, undefined, {
          ...DefaultOptions,
          ...options?.client?.options,
          saveEvents: true,
          includeUtm: true,
          includeReferrer: true,
        });
      } else {
        console.error(
          "ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'"
        );
      }
    });
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param properties The user properties.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  identify(
    userId: string | undefined,
    properties?: IdentifyProperties,
    options?: IdentifyOptions,
    extra?: MiddlewareExtra
  ) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    const event: IdentifyEvent = {
      event_type: SpecialEventType.Identify,
      event_properties: properties,
      user_id: userId || options?.user_id,
      device_id: options?.device_id
    };
    this.runMiddleware({ event, extra }, payload => {
      const e = payload.event;
      if (e.user_id) {
        this.amplitude?.setUserId(e.user_id);
      }
      if (e.device_id) {
        this.amplitude?.setDeviceId(e.device_id);
      }
      const amplitudeIdentify = new amplitude.Identify();
      if (e.event_properties != null) {
        for (const [key, value] of Object.entries(e.event_properties)) {
          amplitudeIdentify.set(key, value);
        }
      }
      this.amplitude?.identify(
        amplitudeIdentify,
        options?.callback,
        // options?.errorCallback
      );
    });
  }

  setGroup(name: string, value: string | string[], options?: GroupOptions, extra?: MiddlewareExtra) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    this.amplitude?.setGroup(name, value);
  }

  /**
   * Identify a group and set group properties.
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  groupIdentify(
    groupType: string,
    groupName: string | string[],
    options?: GroupOptions,
    extra?: MiddlewareExtra,
  ) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    const event: GroupEvent = {
      event_type: SpecialEventType.Group,
      user_id: options?.user_id,
      device_id: options?.device_id
    };
    this.runMiddleware({ event, extra }, payload => {
      const e = payload.event;
      if (e.user_id) {
        this.amplitude?.setUserId(e.user_id);
      }
      if (e.device_id) {
        this.amplitude?.setDeviceId(e.device_id);
      }
      const amplitudeIdentify = new amplitude.Identify();
      if (e.event_properties != null) {
        for (const [key, value] of Object.entries(e.event_properties)) {
          amplitudeIdentify.set(key, value);
        }
      }
      this.amplitude?.groupIdentify(groupType, groupName, amplitudeIdentify, options?.callback);
    });
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  track(event: Event, options?: EventOptions, extra?: MiddlewareExtra) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    this.runMiddleware({ event, extra }, payload => {
      this.amplitude?.logEvent(
        payload.event.event_type,
        payload.event.event_properties,
        options?.callback,
        // options?.errorCallback,
      );
    });
  }

  /**
   * Admin Confirm Delivery
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Confirm%20Delivery)
   *
   * Owner: Mohamed Ali
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminConfirmDelivery(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminConfirmDelivery(), options, extra);
  }

  /**
   * Admin Confirm Inspection
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Confirm%20Inspection)
   *
   * Owner: Mohamed Ali
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminConfirmInspection(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminConfirmInspection(), options, extra);
  }

  /**
   * Admin Refund
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Refund)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminRefund(
    properties: AdminRefundProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminRefund(properties), options, extra);
  }

   /**
   * Admin Resolve Escalation
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Refund)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
   adminResolveEscalation(
    properties: AdminResolveEscalationProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminResolveEscalation(properties), options, extra);
  }

  /**
   * Admin Refund Buyer
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Refund)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminRefundBuyer(
    properties: AdminRefundBuyerProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminRefundBuyer(properties), options, extra);
  }

  /**
   * Admin Release Seller
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Refund)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminReleaseSeller(
    properties: AdminReleaseSellerProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminReleaseSeller(properties), options, extra);
  }

  /**
   * Admin Release
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Release)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminRelease(
    properties: AdminReleaseProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminRelease(properties), options, extra);
  }

  /**
   * Admin Resolve
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Admin%20Resolve)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  adminResolve(
    properties: AdminResolveProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AdminResolve(properties), options, extra);
  }

       /**
   * Ended Broker Contract
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Ended%20Broker%20Contract)
   *
   * When the broker ends a broker contract
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. enableBiometric)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
       endedBrokerContract(
        properties: EndedBrokerContractProperties,
        options?: EventOptions,
        extra?: MiddlewareExtra,
      ) {
        return this.track(new EndedBrokerContract(properties), options, extra);
      }

  /**
   * Submit Phone Number
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/WaffyApp/events/main/latest/Submit%20Phone%20Number)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. userPhoneNumber)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  submitPhoneNumber(
    properties: SubmitPhoneNumberProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new SubmitPhoneNumber(properties), options, extra);
  }


    /**
   * Validated OTP
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/WaffyApp/events/main/latest/Validated%20OTP)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. isOtpValid)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
    validatedOtp(
      properties: ValidatedOTPProperties,
      options?: EventOptions,
      extra?: MiddlewareExtra,
    ) {
      return this.track(new ValidatedOTP(properties), options, extra);
    }


    /**
   * Added Pin code
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/WaffyApp/events/main/latest/Added%20Pin%20Code)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. isOtpValid)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
    addedPincode(
      options?: EventOptions,
      extra?: MiddlewareExtra,
    ) {
      return this.track(new AddedPinCode(), options, extra);
    }


  /**
   * Added Personal info
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/WaffyApp/events/main/latest/Added%20Pin%20Code)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. isOtpValid)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  addedPersonalInfo(
    properties: AddedPersonalInfoProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new AddedPersonalInfo(properties), options, extra);
  }

  /**
   * Confirmed Delivery
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Confirmed%20Delivery)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  confirmedDelivery(
    properties: ConfirmedDeliveryProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new ConfirmedDelivery(properties), options, extra);
  }

  /**
   * Confirmed Inspection
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Confirmed%20Inspection)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  confirmedInspection(
    properties: ConfirmedInspectionProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new ConfirmedInspection(properties), options, extra);
  }

  /**
   * Created Contract
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Created%20Contract)
   *
   * When the 1st party creates and sends a contract to the 2nd party
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractCreationDate)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  createdContract(
    properties: CreatedContractProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new CreatedContract(properties), options, extra);
  }

  /**
   * Deleted Contract
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Deleted%20Contract)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractDeletionDate)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  deletedContract(
    properties: DeletedContractProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new DeletedContract(properties), options, extra);
  }

  /**
   * Escalated
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Escalated)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. additionalEscalationReasons)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  escalated(
    properties: EscalatedProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new Escalated(properties), options, extra);
  }

  sentFeedback(
    properties: SentFeedbackProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ){
    return this.track(new SentFeedback(properties), options, extra);
  }
  /**
   * Payed Contract
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Payed%20Contract)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  payedContract(
    properties: PayedContractProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new PayedContract(properties), options, extra);
  }

  /**
   * Shared Contract
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Shared%20Contract)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  sharedContract(
    properties: SharedContractProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new SharedContract(properties), options, extra);
  }

  /**
   * Signed Up
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Signed%20Up)
   *
   * When the seller/buyer creates an account
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. enableBiometric)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  signedUp(
    properties: SignedUpProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new SignedUp(properties), options, extra);
  }

  /**
   * Verified Via Absher
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Verified%20Via%20Absher)
   *
   * User verivies his identity through Absher to be able to create and receive contracts
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. absherPageSource)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  verifiedViaAbsher(
    properties: VerifiedViaAbsherProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new VerifiedViaAbsher(properties), options, extra);
  }

  /**
   * Viewed Contract Details Page
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Viewed%20Contract%20Details%20Page)
   *
   * Owner: Mohamed Ali
   *
   * @param properties The event's properties (e.g. contractId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  viewedContractDetailsPage(
    properties: ViewedContractDetailsPageProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new ViewedContractDetailsPage(properties), options, extra);
  }

  /**
   * Viewed Contract Wizard Page
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Viewed%20Contract%20Wizard%20Page)
   *
   * Owner: Mohamed Ali
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  viewedContractWizardPage(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new ViewedContractWizardPage(), options, extra);
  }

  /**
   * Viewed Sign Up Page
   *
   * [View in Tracking Plan](https://data.amplitude.com/gotrah/Waffy/events/main/latest/Viewed%20Sign%20Up%20Page)
   *
   * Owner: Mohamed Ali
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  viewedSignUpPage(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new ViewedSignUpPage(), options, extra);
  }


  addEventMiddleware(middleware: Middleware): void {
    this.middlewares.push(middleware);
  }

  private runMiddleware(payload: MiddlewarePayload, next: MiddlewareNext): void {
    let curMiddlewareIndex = -1;
    const middlewareCount = this.middlewares.length;

    const middlewareNext: MiddlewareNext = curPayload => {
      curMiddlewareIndex += 1;
      if (curMiddlewareIndex < middlewareCount) {
        this.middlewares[curMiddlewareIndex](curPayload, _next);
      } else {
        next(curPayload);
      }
    };

    const _next: MiddlewareNext = middlewareCount > 0 ? middlewareNext : next;

    _next(payload);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type ConfigExt = Partial<Config> & { plan?: Plan };

export type Plan = {
  branch?: string;
  source?: string;
  version?: string;
  versionId?: string;
};

export enum SpecialEventType {
  Identify = "Identify",
  Group = "Group",
}

export type BaseEvent = {
  event_type: string;
  event_properties?: { [key: string]: any };
  plan?: Plan;
  user_id?: string;
  device_id?: string;
};
export type IdentifyEvent = BaseEvent & {
  event_type: SpecialEventType.Identify;
};
export type GroupEvent = BaseEvent & { event_type: SpecialEventType.Group };
export type Event = BaseEvent | IdentifyEvent | GroupEvent;

type BaseEventOptions = Omit<BaseEvent, "event_type" | "event_properties"> & {
  callback: Callback;
  errorCallback: Callback;
};
export type EventOptions = BaseEventOptions;
export type IdentifyOptions = BaseEventOptions;
export type GroupOptions = BaseEventOptions;

/**
 * Unstructured object to let users pass extra data to middleware
 */
export interface MiddlewareExtra {
  [name: string]: any;
}

/**
 * Data to be processed by middleware
 */
export interface MiddlewarePayload {
  event: Event;
  extra?: MiddlewareExtra;
}

/**
 * Function called at the end of each Middleware to run the next middleware in the chain
 */
export type MiddlewareNext = (payload: MiddlewarePayload) => void;

/**
 * A function to run on the Event stream (each logEvent call)
 *
 * @param payload The event and extra data being sent
 * @param next Function to run the next middleware in the chain, not calling next will end the middleware chain
 */
export type Middleware = (
  payload: MiddlewarePayload,
  next: MiddlewareNext
) => void;
